/**
 * Binar aralashma faza muvozanati (bug'–suyuqlik).
 *
 * MANBA: Kasatkin A.G., "Osnovnye protsessy i apparaty", gl. XVII (rektifikatsiya);
 *        Pavlov, Romankov, Noskov, gl. 7; McCabe, Smith, Harriott, Ch. 21;
 *        Perry's Chemical Engineers' Handbook, 8th ed., Sec. 13 (VLE).
 *
 * (a) IDEAL RAOULT (Antoine orqali):
 *       P = x·P1sat(T) + (1 − x)·P2sat(T)         — qaynash harorati T (bisection)
 *       y = x·P1sat(T)/P
 *     Teskari (shudring nuqtasi):
 *       y·P/P1sat(T) + (1 − y)·P/P2sat(T) = 1     — T (bisection),  x = y·P/P1sat(T)
 *     Lokal nisbiy uchuvchanlik α(T) = P1sat/P2sat.
 * (b) O'ZGARMAS α:
 *       y = αx / (1 + (α − 1)x),   x = y / (α − (α − 1)y)
 *
 * ASSUMPTION:
 *   1. Bug' fazasi ideal gaz, suyuqlik fazasi ideal eritma (aktivlik koeffitsienti γ = 1).
 *      Etanol–suv, metanol–suv, sirka kislota–suv kabi noideal juftliklar uchun Raoult
 *      xato beradi — `column.nonIdealMixture` ogohlantirish; foydalanuvchi α bersin.
 *   2. O'zgarmas α rejimida haroratlar baribir Raoult qaynash nuqtasidan olinadi
 *      (issiqlik va gidravlika uchun taxminiy). α ni foydalanuvchi bergan bo'lsa,
 *      T egri chizig'i muvozanat egri chizig'i bilan qat'iy mos kelmaydi.
 *   3. Kolonna bosimi barcha bosqichlarda bir xil (bosim yo'qotish yo'q).
 *   4. Azeotrop yo'q: α > 1 butun oraliqda (Raoult uchun bu avtomatik — P1sat > P2sat).
 */
import { getSubstance, saturationPressure, saturationTemperature } from '../../properties';
import { bisection } from '../../solvers';
import type { VleMode, XY } from './types';

export interface BinaryVle {
  mode: VleMode;
  /** Muvozanat bug' tarkibi y*(x) */
  y(x: number): number;
  /** Teskari: suyuqlik tarkibi x(y) muvozanatda */
  x(y: number): number;
  /** Qaynash (bubble) harorati, K */
  bubbleT(x: number): number;
  /** Shudring (dew) harorati, K */
  dewT(y: number): number;
  /** Lokal nisbiy uchuvchanlik x da */
  alpha(x: number): number;
  /** Yengil komponent normal qaynash harorati berilgan bosimda, K */
  TbLight: number;
  TbHeavy: number;
}

/** Raoult qonuni aniq xato beradigan ma'lum noideal juftliklar */
const NON_IDEAL_PAIRS: ReadonlySet<string> = new Set([
  'ethanol|water',
  'methanol|water',
  'acetic-acid|water',
  'ethanol|acetic-acid',
  'methanol|acetic-acid',
  'ethanol|benzene',
  'methanol|benzene',
  'ethanol|toluene',
  'methanol|toluene',
]);

export function isKnownNonIdealPair(a: string, b: string): boolean {
  return NON_IDEAL_PAIRS.has(`${a}|${b}`) || NON_IDEAL_PAIRS.has(`${b}|${a}`);
}

const clamp01 = (v: number): number => Math.min(1, Math.max(0, v));
const T_TOL = 1e-7;

export function makeVle(light: string, heavy: string, P: number, alphaConst?: number): BinaryVle {
  getSubstance(light);
  getSubstance(heavy);
  const TbLight = saturationTemperature(light, P);
  const TbHeavy = saturationTemperature(heavy, P);
  if (!(TbLight < TbHeavy)) {
    throw new Error(
      'rectification-column: lightComponent must be more volatile than heavyComponent at column pressure',
    );
  }
  const P1 = (T: number): number => saturationPressure(light, T);
  const P2 = (T: number): number => saturationPressure(heavy, T);

  const bubbleT = (x: number): number => {
    const xc = clamp01(x);
    if (xc >= 1) return TbLight;
    if (xc <= 0) return TbHeavy;
    const r = bisection((T) => xc * P1(T) + (1 - xc) * P2(T) - P, TbLight, TbHeavy, {
      xtol: T_TOL,
      ftol: 0,
    });
    return r.value;
  };
  const dewT = (y: number): number => {
    const yc = clamp01(y);
    if (yc >= 1) return TbLight;
    if (yc <= 0) return TbHeavy;
    const r = bisection((T) => (yc * P) / P1(T) + ((1 - yc) * P) / P2(T) - 1, TbLight, TbHeavy, {
      xtol: T_TOL,
      ftol: 0,
    });
    return r.value;
  };

  if (alphaConst !== undefined) {
    if (!(alphaConst > 1)) throw new Error('rectification-column: relativeVolatility must be > 1');
    const a = alphaConst;
    return {
      mode: 'constant-alpha',
      y: (x) => {
        const xc = clamp01(x);
        return (a * xc) / (1 + (a - 1) * xc);
      },
      x: (y) => {
        const yc = clamp01(y);
        return yc / (a - (a - 1) * yc);
      },
      bubbleT,
      dewT,
      alpha: () => a,
      TbLight,
      TbHeavy,
    };
  }

  return {
    mode: 'raoult',
    y: (x) => {
      const xc = clamp01(x);
      if (xc >= 1) return 1;
      if (xc <= 0) return 0;
      return clamp01((xc * P1(bubbleT(xc))) / P);
    },
    x: (y) => {
      const yc = clamp01(y);
      if (yc >= 1) return 1;
      if (yc <= 0) return 0;
      return clamp01((yc * P) / P1(dewT(yc)));
    },
    bubbleT,
    dewT,
    alpha: (x) => {
      const T = bubbleT(x);
      return P1(T) / P2(T);
    },
    TbLight,
    TbHeavy,
  };
}

/** Diagramma uchun muvozanat egri chizig'i, n+1 nuqta */
export function equilibriumCurve(vle: BinaryVle, n = 50): XY[] {
  const pts: XY[] = [];
  for (let i = 0; i <= n; i++) {
    const x = i / n;
    pts.push({ x, y: vle.y(x) });
  }
  return pts;
}
